import { useMemo } from "react";
import { useAppSelector } from "@store/hooks";

// Define the keys for the side tabs in the Service Agreement Policy UI.
export type KeysForPlanSideTabs = "PLAN_OPTIONS_SIDE_TAB" | "ADDRESS_SIDE_TAB" | "PROPERTY_SIDE_TAB" | "BOILER_SIDE_TAB" | "PROMO_DISCOUNT_SIDE_TAB";

// Type definition for the indexes of the side tabs.
type SideTabsIndexes = {
    [key in KeysForPlanSideTabs]: number;
};

// Type definition for the state (validity) of each side tab.
type SideTabsStates = {
    [key in KeysForPlanSideTabs]: boolean;
};

/**
 * Custom hook for managing the state and behavior of side tabs in the Service Agreement Policy UI.
 * This hook encapsulates logic for determining the current active tab, its validity, and handling specific
 * UI logic based on the type of service agreement (e.g., whether it's a boiler plan).
 *
 * @returns {Object} An object containing various states and configurations related to the side tabs.
 */
export const useCarePlanPlanDetailsSideTab = () => {
    // State selectors from the Redux store.
    const serviceAgreementPolicyTab = useAppSelector(({ ui }) => ui.CarePlanPlanDetailsActiveSideTab);
    const isBoiler = useAppSelector(({ ui }) => ui.CarePlanPlanDetailsIsBoiler);
    const isAddressValid = useAppSelector(({ state }) => state.CarePlanPlanDetailsAddressValid);
    const isPlanTypeValid = useAppSelector(({ state }) => state.CarePlanPlanDetailsPlanOptionsValid);
    const isPropertyValid = useAppSelector(({ state }) => state.CarePlanPlanDetailsPropertyValid);
    const isBoilerValid = useAppSelector(({ state }) => state.CarePlanPlanDetailsBoilerValid);
    
    // Calculate the index for the promo discount tab based on whether it's a boiler plan.
    const PROMO_DISCOUNT_SIDE_TAB = isBoiler ? 4 : 3;
    
    // useMemo to optimize the computation of the side tabs' indexes.
    const sideTabsIndexes = useMemo<SideTabsIndexes>(() => ({
        PLAN_OPTIONS_SIDE_TAB: 0,
        ADDRESS_SIDE_TAB: 1,
        PROPERTY_SIDE_TAB: 2,
        BOILER_SIDE_TAB: 3,
        PROMO_DISCOUNT_SIDE_TAB
    }), [PROMO_DISCOUNT_SIDE_TAB]);

    // useMemo to optimize the computation of the state (validity) of each side tab.
    const sideTabsStates = useMemo<SideTabsStates>(() => ({
        PLAN_OPTIONS_SIDE_TAB: isPlanTypeValid,
        ADDRESS_SIDE_TAB: isAddressValid,
        PROPERTY_SIDE_TAB: isPropertyValid,
        BOILER_SIDE_TAB: isBoilerValid,
        PROMO_DISCOUNT_SIDE_TAB: true
    }), [isAddressValid, isBoilerValid, isPropertyValid, isPlanTypeValid]);
    
    // Return the computed states and configurations.
    return { serviceAgreementPolicyTab, sideTabsIndexes, sideTabsStates, isBoiler };
};
